import { type FormEvent, useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { apiGet, apiPost } from "@/lib/apiClient";
import { formatCrmStatusLabel } from "@/lib/crmDisplay";
import { ArrowLeft } from "lucide-react";

type AccountOption = { id: string; name: string };

export function CrmDealNewPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const presetAccountId = searchParams.get("accountId")?.trim() || "";

  const [accounts, setAccounts] = useState<AccountOption[]>([]);
  const [accountId, setAccountId] = useState("");
  const [name, setName] = useState("");
  const [amount, setAmount] = useState("");
  const [stage, setStage] = useState("qualification");
  const [status, setStatus] = useState<"open" | "won" | "lost">("open");
  const [closeDate, setCloseDate] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const res = await apiGet<{ accounts?: { id: string; name: string }[] }>("/api/crm/accounts?fetchAll=true");
      if (cancelled || !res.ok) return;
      setAccounts((res.data?.accounts ?? []).map((a) => ({ id: a.id, name: a.name })));
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (presetAccountId && !accountId) setAccountId(presetAccountId);
  }, [presetAccountId, accountId]);

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setError(null);
    setIsSubmitting(true);
    try {
      if (!accountId) throw new Error("Select an account.");
      if (!name.trim()) throw new Error("Deal name is required.");
      const res = await apiPost<{ id?: string }>("/api/crm/deals", {
        accountId,
        name: name.trim(),
        amount: amount.trim() ? Number(amount) : null,
        stage,
        status,
        closeDate: closeDate || null,
      });
      if (!res.ok || !res.data?.id) throw new Error(res.error || "Failed to create deal.");
      void navigate(
        `/crm/deals/${encodeURIComponent(res.data.id)}?accountId=${encodeURIComponent(accountId)}`,
      );
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to create deal.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="space-y-6">
      <Link
        to="/crm/deals"
        className="inline-flex items-center gap-2 text-sm font-medium text-indigo-600 hover:text-indigo-500 dark:text-indigo-300"
      >
        <ArrowLeft className="h-4 w-4" aria-hidden />
        All deals
      </Link>
      <h1 className="text-2xl font-semibold">Create new deal</h1>
      {error ? (
        <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">{error}</div>
      ) : null}
      <form onSubmit={(e) => void handleSubmit(e)} className="max-w-md space-y-3">
        <select
          required
          value={accountId}
          onChange={(e) => setAccountId(e.target.value)}
          className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm dark:border-white/20 dark:bg-black/20"
        >
          <option value="">Select account…</option>
          {accounts.map((a) => (
            <option key={a.id} value={a.id}>
              {a.name}
            </option>
          ))}
        </select>
        <input
          required
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Deal name"
          className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm dark:border-white/20 dark:bg-black/20"
        />
        <input
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          placeholder="Amount"
          type="number"
          step="0.01"
          className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm dark:border-white/20 dark:bg-black/20"
        />
        <div className="grid gap-3 sm:grid-cols-2">
          <select
            value={stage}
            onChange={(e) => setStage(e.target.value)}
            className="rounded-md border border-gray-300 px-3 py-2 text-sm dark:border-white/20 dark:bg-black/20"
          >
            {["qualification", "proposal", "negotiation", "closed_won", "closed_lost"].map((s) => (
              <option key={s} value={s}>
                {formatCrmStatusLabel(s)}
              </option>
            ))}
          </select>
          <select
            value={status}
            onChange={(e) => setStatus(e.target.value as "open" | "won" | "lost")}
            className="rounded-md border border-gray-300 px-3 py-2 text-sm dark:border-white/20 dark:bg-black/20"
          >
            <option value="open">Open</option>
            <option value="won">Won</option>
            <option value="lost">Lost</option>
          </select>
        </div>
        <input
          type="date"
          value={closeDate}
          onChange={(e) => setCloseDate(e.target.value)}
          className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm dark:border-white/20 dark:bg-black/20"
        />
        <button
          type="submit"
          disabled={isSubmitting}
          className="rounded-md bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-500 disabled:opacity-50"
        >
          {isSubmitting ? "Creating…" : "Create deal"}
        </button>
      </form>
    </div>
  );
}
